import { useLocation, useNavigate, useParams } from "react-router-dom";
import React, { useEffect, useContext, useState, useCallback, useRef } from "react";
import { AuthContext } from "../../context/authContext";
import {
  Box, Container, Typography, Grid, Chip,
  TextField, Breadcrumbs, useMediaQuery, useTheme, Snackbar, Alert, CircularProgress,
} from "@mui/material";
import { NavigateNext } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ScaleOutlinedIcon from "@mui/icons-material/ScaleOutlined";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import CloudSyncOutlinedIcon from "@mui/icons-material/CloudSyncOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Loading from "../../components/Loading/Loading";
import {
  getCarritoByID, updateCarritoByID, deleteCarritoByID,
} from "../../services/CarritoServices";
import {
  postFavorito, deleteFavorito, getFavoritosByUserID,
} from "../../services/FavoritosServices";
import { getArticuloById } from "../../services/ArticulosServices";
import QuantityControl from "../../components/ProductCard/ProductButtonsBuy";
import logoDefault from "../../images/productDefault.jpg";

// ─── Badge de estado ───
const getInstallmentStyle = (installment = "") => {
  const v = installment.toLowerCase();
  if (v.includes("sin stock")) return { bg: "#fee2e2", color: "#dc2626", border: "#fca5a5" };
  if (v.includes("oferta")) return { bg: "#d1fae5", color: "#059669", border: "#6ee7b7" };
  if (v.includes("últimas") || v.includes("ultimas")) return { bg: "#fff7ed", color: "#ea580c", border: "#fed7aa" };
  if (v.includes("lista")) return { bg: "#fdf4ff", color: "#9333ea", border: "#e9d5ff" };
  if (v.includes("disponible")) return { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" };
  return { bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" };
};

// ─── Mapper ───
const mapArticulo = (art, empresa) => {
  const backendUrl = process.env.REACT_APP_URL_BACKEND;
  const imgArt = art?.art_imagen?.trim();
  const imgEmp = empresa?.imagen_articulo?.trim();
  const imgSrc = imgArt ? `${backendUrl}${imgArt}`
    : imgEmp ? `${backendUrl}${imgEmp}`
      : logoDefault;

  const precioMostrar = art?.precioMostrar ?? art?.art_nprecioventa ?? 0;
  const precioVigente = art?.precioVigente ?? art?.art_nprecioventa ?? 0;
  const stock = art?.stock ?? art?.artic_deposito?.reduce((t, d) => t + (d.ade_cantidadexi || 0), 0) ?? 0;
  const hayOferta = precioMostrar !== precioVigente && precioVigente > 0;
  const tipoPrecio = art?.tipoPrecioFinal;

  let textoPrecio;
  switch (tipoPrecio) {
    case 2: textoPrecio = "Oferta"; break;
    case 1: textoPrecio = "Lista"; break;
    default: textoPrecio = "Disponible"; break;
  }

  const installment = empresa?.venta_sin_stock === false
    ? (stock > 0 ? `${textoPrecio}: ${stock} u` : "Sin Stock")
    : textoPrecio;

  const price = precioMostrar
    ? "$" + precioMostrar.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "Precio no disponible";

  const priceOld = hayOferta
    ? "$" + precioVigente.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : null;

  const percentage = hayOferta && precioVigente > 0
    ? (((precioVigente - precioMostrar) / precioVigente) * 100)
      .toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    : null;

  return {
    id: art.id, code: art.art_codigo,
    title: art.art_descripcion ?? "Sin título",
    description: art.art_desclarga ?? "",
    category: art?.Ramo?.descripcion_ramo ?? art?.ramo ?? "",
    imgSrc, urls: imgSrc ? [imgSrc] : [],
    price, priceOld, percentage, installment,
    stockActual: stock,
    availability: art.art_desclarga ?? "",
    isStockFractional: art.art_fraccionable === 1,
  };
};

// ─── SVG Icons ───
const WhatsAppIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);
const FacebookIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);
const InstagramIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
  </svg>
);

// ─── ShareButtons ───
const ShareButtons = ({ title, price, onCopy }) => {
  const url = window.location.href;
  const text = `${title} - ${price}\n${url}`;
  const buttons = [
    { label: "WhatsApp", color: "#25D366", icon: <WhatsAppIcon />, href: `https://wa.me/?text=${encodeURIComponent(text)}` },
    { label: "Facebook", color: "#1877F2", icon: <FacebookIcon />, href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` },
    { label: "Instagram", color: "#E1306C", icon: <InstagramIcon />, onClick: () => { navigator.clipboard.writeText(url); onCopy("¡Enlace copiado! Pegalo en tu historia de Instagram"); } },
    { label: "Copiar", color: "var(--text-secondary)", icon: <ContentCopyIcon sx={{ fontSize: 14 }} />, onClick: () => { navigator.clipboard.writeText(url); onCopy(); } },
  ];
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.5 }}>
      {buttons.map(({ label, color, href, onClick, icon }) => (
        <Box key={label} component={href ? "a" : "button"} href={href ?? undefined} target={href ? "_blank" : undefined} rel={href ? "noopener noreferrer" : undefined} onClick={onClick ?? undefined}
          sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1.5, py: 0.6, borderRadius: "var(--radius-md)", border: `1.5px solid ${color}`, color, bgcolor: "transparent", fontSize: "0.75rem", fontWeight: 600, fontFamily: "var(--font-body)", cursor: "pointer", textDecoration: "none", transition: "all 0.15s", "&:hover": { bgcolor: color, color: "#fff", "& svg": { fill: "#fff" } } }}>
          {icon}{label}
        </Box>
      ))}
    </Box>
  );
};

// ─── Componente principal ───
const Product = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { productID: paramId } = useParams();
  const { addToCart, user, empresa } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const stateData = location?.state?.productData;

  const [productData, setProductData] = useState(stateData ?? null);
  const [rawArticulo, setRawArticulo] = useState(null);
  const [loading, setLoading] = useState(!stateData);
  const [cartItems, setCartItems] = useState([]);
  const [favoritosItems, setFavoritosItems] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [imageIndex, setImageIndex] = useState(0);
  const [revImgArr, setRevImgArr] = useState([]);
  const [amount, setAmount] = useState(1);
  const [observation, setObservation] = useState("");
  const [bgPosition, setBgPosition] = useState("50% 50%");
  const [loadingCart, setLoadingCart] = useState(false);
  const [refreshCarrito, setRefreshCarrito] = useState(0);
  const [idItem, setIdItem] = useState(null);
  const [snackCopy, setSnackCopy] = useState(false);
  const [snackMsg, setSnackMsg] = useState("Enlace copiado al portapapeles");
  const [bgSize, setBgSize] = useState("contain");

  // ── Observación: estado visual ──
  // 'idle' | 'typing' | 'saving' | 'saved' | 'added' | 'modified' | 'deleted' | 'error'
  const [obsStatus, setObsStatus] = useState('idle');
  const [savedObs, setSavedObs] = useState(''); // la obs que tiene el server
  const obsTimerRef = useRef(null);

  // ── Fetch por ID ──
  const fetchById = useCallback(async () => {
    if (!paramId) return;
    setLoading(true);
    try {
      const res = await getArticuloById(paramId);
      if (res) { setRawArticulo(res); setProductData(mapArticulo(res, empresa)); }
      console.log("res", res)
    } catch (e) { console.error("Error al obtener artículo:", e); }
    finally { setLoading(false); }
  }, [paramId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (stateData) { setProductData(stateData); setLoading(false); return; }
    fetchById();
  }, [paramId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (rawArticulo && empresa) { setProductData(mapArticulo(rawArticulo, empresa)); setLoading(false); } }, [empresa, rawArticulo]);

  // ── Imágenes ──
  useEffect(() => {
    if (productData) { setRevImgArr(productData.urls?.slice().reverse() || []); setAmount(productData.cantItem || 1); }
    return () => setRevImgArr([]);
  }, [productData]);

  // ── Carrito ──
  const fetchCarrito = useCallback(async () => {
    const idCarrito = localStorage.getItem("idCarrito");
    if (!idCarrito || !productData?.id) return;
    try {
      const res = await getCarritoByID(idCarrito);
      if (res?.Carrito_items) {
        const items = res.Carrito_items.map(item => ({ cartId: res.id, itemId: item.id, artId: item.art_Id, cant: item.cantidad, obs: item.observacion || "" }));
        setCartItems(items);
        const found = items.find(i => i.artId === productData.id);
        if (found) {
          setIdItem(found.itemId);
          setQuantities(p => ({ ...p, [productData.id]: found.cant }));
          setObservation(found.obs);
          setSavedObs(found.obs);
          if (found.obs) setObsStatus('saved');
        }
        else { setIdItem(null); }
      }
    } catch (e) { console.error(e); }
  }, [productData?.id]);

  // ── Favoritos ──
  const fetchFavoritos = useCallback(async () => {
    if (!user?.id) return;
    try { const res = await getFavoritosByUserID(user.id); setFavoritosItems(res?.map(i => ({ favId: i.id, artId: i.art_Id, cliId: i.user_id })) || []); }
    catch (e) { console.error(e); }
  }, [user?.id]);

  useEffect(() => { if (user?.id) { fetchFavoritos(); fetchCarrito(); } }, [user?.id, fetchFavoritos, fetchCarrito, refreshCarrito]);

  // ── Derivados ──
  const isInCart = !!cartItems.find(i => i.artId === productData?.id);
  const isInFav = !!favoritosItems.find(i => i.artId === productData?.id);
  const idItemFav = favoritosItems.find(i => i.artId === productData?.id)?.favId || null;
  const stockLimit = productData?.stockActual || 10;
  const cantItem = quantities[productData?.id] || 1;
  const instStyle = getInstallmentStyle(productData?.installment || "");
  const isOferta = productData?.installment?.toLowerCase().includes("oferta");
  const isStockAvail = productData?.stockActual > 0;
  const isLogin = !!user?.id;
  const isCheckStock = empresa?.venta_sin_stock;
  const isStockFractional = productData?.fractional || false;
  console.log("artituclo", productData)
  console.log('isstock', isStockFractional)
  // ── Handlers ──
  const handleQtyChange = async (itemId, newQty, productId) => {
    setQuantities(p => ({ ...p, [productId]: newQty }));
    if (!itemId) return;
    setLoadingCart(true);
    try {
      if (newQty > 0) await updateCarritoByID(itemId, { cantidad: newQty, observacion: "" });
      else await deleteCarritoByID(itemId);
      setRefreshCarrito(p => p + 1);
    } catch (e) { console.error(e); }
    finally { setLoadingCart(false); }
  };

  const handleCartAdd = async (qty) => {
    if (!productData?.id) return;
    try {
      await addToCart(productData.id, qty || amount, observation);
      setRefreshCarrito(p => p + 1);
    } catch (e) { console.error(e); }
  }; const handleCartDelete = async () => { if (!idItem) return; try { await deleteCarritoByID(idItem, user?.id); setRefreshCarrito(p => p + 1); } catch (e) { console.error(e); } };
  const handleAddFav = async () => { if (!productData?.id) return; try { await postFavorito({ art_Id: productData.id, user_id: user?.id }); await fetchFavoritos(); } catch (e) { console.error(e); } };
  const handleDeleteFav = async () => { try { await deleteFavorito(idItemFav); await fetchFavoritos(); } catch (e) { console.error(e); } };

  // ── Auto-save observación con debounce ──
  const handleObsChange = (e) => {
    if (e.target.value.length > 1000) return;
    const newVal = e.target.value;
    setObservation(newVal);
    setObsStatus('typing');

    if (obsTimerRef.current) clearTimeout(obsTimerRef.current);
    obsTimerRef.current = setTimeout(async () => {
      if (!isInCart || !idItem) return;
      setObsStatus('saving');
      try {
        await updateCarritoByID(idItem, { cantidad: cantItem, observacion: newVal });
        setSavedObs(newVal);
        if (newVal === '') setObsStatus('deleted');
        else if (savedObs === '') setObsStatus('added');
        else setObsStatus('modified');
        // Volver a 'saved' después de 2.5s
        setTimeout(() => setObsStatus(newVal ? 'saved' : 'idle'), 2500);
      } catch (e) {
        console.error("Error guardando observación:", e);
        setObsStatus('error');
        setTimeout(() => setObsStatus('idle'), 3000);
      }
    }, 800);
  };

  // Limpiar timer al desmontar
  useEffect(() => () => { if (obsTimerRef.current) clearTimeout(obsTimerRef.current); }, []);

  const step = isStockFractional ? 0.25 : 1;
  const increase = () => {
    if (isInCart) handleQtyChange(idItem, Math.round((cantItem + step) * 100) / 100, productData.id);
    else handleCartAdd();
  };
  const decrease = () => {
    if (isInCart) {
      const next = Math.round((cantItem - step) * 100) / 100;
      if (next >= (isStockFractional ? 0.01 : 1)) handleQtyChange(idItem, next, productData.id);
    }
  };

  if (loading) return <Loading message="Cargando artículo..." />;
  if (!productData) return <Loading message="Artículo no encontrado..." />;

  const resetZoom = () => { setBgPosition("center"); setBgSize("contain"); };
  const zoom = (e) => {
    const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
    setBgPosition(`${((e.clientX - left) / width) * 100}% ${((e.clientY - top) / height) * 100}%`);
    setBgSize("200%");
  };

  return (
    <Box sx={{ bgcolor: "var(--bg-main)", minHeight: "100vh", pb: 6 }}>
      <Container maxWidth="lg" sx={{ pt: 3 }}>

        {/* Breadcrumb */}
        <Breadcrumbs separator={<NavigateNext sx={{ fontSize: 16, color: "var(--border-hover)" }} />} sx={{ mb: 3 }}>
          <Box onClick={() => navigate(-1)} sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer", color: "var(--accent)", fontWeight: 600, fontSize: "0.82rem", "&:hover": { color: "var(--accent-hover)" } }}>
            <ArrowBackIcon sx={{ fontSize: 15 }} /> Volver
          </Box>
          <Typography sx={{ fontSize: "0.82rem", color: "var(--text-muted)" }}>Home</Typography>
          {productData.category && <Typography sx={{ fontSize: "0.82rem", color: "var(--text-muted)" }}>{productData.category}</Typography>}
          <Typography sx={{ fontSize: "0.82rem", color: "var(--text-primary)", fontWeight: 600, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{productData.title}</Typography>
        </Breadcrumbs>

        {/* Card */}
        <Box sx={{ bgcolor: "var(--bg-card)", borderRadius: "var(--radius-xl)", border: "1.5px solid var(--border)", overflow: "hidden", boxShadow: "var(--shadow-md)" }}>
          <Grid container>

            {/* ── Galería ── */}
            <Grid item xs={12} md={5} sx={{ bgcolor: "var(--bg-subtle)", borderRight: { md: "1.5px solid var(--border)" } }}>
              <Box sx={{ position: "relative", p: { xs: 2, md: 3 } }}>
                {isInFav && (
                  <Box sx={{ position: "absolute", top: 16, left: 16, zIndex: 2, width: 32, height: 32, borderRadius: "50%", bgcolor: "#fdf2f8", border: "1.5px solid #f9a8d4", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <FavoriteIcon sx={{ fontSize: 16, color: "#ec4899" }} />
                  </Box>
                )}
                {isInCart && (
                  <Box sx={{ position: "absolute", top: 16, right: 16, zIndex: 2, width: 32, height: 32, borderRadius: "50%", bgcolor: "#f0fdf4", border: "1.5px solid #86efac", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <CheckCircleIcon sx={{ fontSize: 18, color: "#16a34a" }} />
                  </Box>
                )}

                <Box onMouseMove={zoom} onMouseLeave={resetZoom}
                  sx={{
                    width: "100%", height: { xs: 260, md: 360 }, borderRadius: "var(--radius-lg)",
                    overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center",
                    bgcolor: "var(--bg-card)", border: "1.5px solid var(--border)", cursor: "zoom-in",
                    backgroundImage: revImgArr[imageIndex] ? `url(${revImgArr[imageIndex]})` : "none",
                    backgroundPosition: bgPosition, backgroundSize: bgSize, backgroundRepeat: "no-repeat",
                  }}>
                  {revImgArr[imageIndex] ? (
                    <Box component="img" src={revImgArr[imageIndex]} alt={productData.title} sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", opacity: 0 }} />
                  ) : (
                    <Typography sx={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>Sin imagen</Typography>
                  )}
                </Box>

                {revImgArr.length > 1 && (
                  <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap", justifyContent: "center" }}>
                    {revImgArr.map((url, i) => (
                      <Box key={i} onClick={() => setImageIndex(i)} sx={{ width: 54, height: 54, borderRadius: "var(--radius-md)", overflow: "hidden", cursor: "pointer", border: i === imageIndex ? "2px solid var(--accent)" : "1.5px solid var(--border)", bgcolor: "var(--bg-card)", flexShrink: 0, transition: "border-color 0.15s", "&:hover": { borderColor: "var(--accent)" } }}>
                        <Box component="img" src={url} alt={`img-${i}`} sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
                      </Box>
                    ))}
                  </Box>
                )}

                {isLogin && (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                    <Box onClick={() => isInFav ? handleDeleteFav() : handleAddFav()} sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 2, py: 0.75, borderRadius: "var(--radius-md)", cursor: "pointer", border: `1.5px solid ${isInFav ? "#f9a8d4" : "var(--border)"}`, bgcolor: isInFav ? "#fdf2f8" : "var(--bg-subtle)", color: isInFav ? "#ec4899" : "var(--text-secondary)", fontSize: "0.8rem", fontWeight: 600, fontFamily: "var(--font-body)", transition: "all 0.15s", "&:hover": { borderColor: "#f9a8d4", bgcolor: "#fdf2f8", color: "#ec4899" } }}>
                      {isInFav ? <><FavoriteIcon sx={{ fontSize: 16 }} /> Quitar favorito</> : <><FavoriteBorderIcon sx={{ fontSize: 16 }} /> Agregar a favoritos</>}
                    </Box>
                  </Box>
                )}

                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "var(--font-body)", mb: 0.75 }}>Compartir</Typography>
                  <ShareButtons title={productData.title} price={productData.price} onCopy={(msg) => { setSnackMsg(msg || "Enlace copiado al portapapeles"); setSnackCopy(true); }} />
                </Box>
              </Box>
            </Grid>

            {/* ── Info ── */}
            <Grid item xs={12} md={7}>
              <Box sx={{ p: { xs: 2.5, md: 4 }, display: "flex", flexDirection: "column", gap: 2 }}>

                <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "var(--font-body)" }}>Código: {productData.code}</Typography>
                <Typography sx={{ fontFamily: "var(--font-heading)", fontWeight: 800, fontSize: { xs: "1.3rem", md: "1.6rem" }, color: "var(--text-primary)", lineHeight: 1.2, letterSpacing: "-0.02em" }}>{productData.title}</Typography>
                {productData.description && <Typography sx={{ fontFamily: "var(--font-body)", fontSize: "0.875rem", color: "var(--text-secondary)", lineHeight: 1.65 }}>{productData.description}</Typography>}

                {/* Precio */}
                <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2, flexWrap: "wrap" }}>
                  <Box>
                    <Typography sx={{ fontFamily: "var(--font-heading)", fontWeight: 800, fontSize: { xs: "1.75rem", md: "2.2rem" }, color: "var(--accent)", lineHeight: 1 }}>{productData.price}</Typography>
                    <Typography sx={{ fontFamily: "var(--font-body)", fontSize: "0.7rem", color: "var(--text-muted)", mt: 0.5 }}>IVA incluido</Typography>
                  </Box>
                  {isOferta && (productData.percentage || productData.priceOld) && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      {productData.percentage && <Chip label={`${productData.percentage}% OFF`} size="small" sx={{ bgcolor: "#d1fae5", color: "#059669", fontWeight: 700, fontSize: "0.72rem", height: 22, border: "1px solid #6ee7b7" }} />}
                      {productData.priceOld && <Typography sx={{ fontFamily: "var(--font-mono)", fontSize: "0.875rem", color: "var(--text-muted)", textDecoration: "line-through" }}>{productData.priceOld}</Typography>}
                    </Box>
                  )}
                </Box>

                {/* Badges estado + fraccionable */}
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {productData.installment && (
                    <Chip label={productData.installment} size="small" sx={{ bgcolor: instStyle.bg, color: instStyle.color, border: `1px solid ${instStyle.border}`, fontWeight: 600, fontSize: "0.78rem", height: 26, borderRadius: "var(--radius-sm)", fontFamily: "var(--font-body)" }} />
                  )}
                  {isStockFractional && (
                    <Chip
                      icon={
                        <ScaleOutlinedIcon
                          sx={{
                            fontSize: 14,
                            color: "#fdfdfd !important",
                          }}
                        />
                      }
                      label="Fraccionable"
                      size="small"
                      sx={{
                        bgcolor: "#09566d",
                        color: "#faf5ff",
                        border: "1px solid #e9d5ff",
                        fontWeight: 600,
                        fontSize: "0.78rem",
                        height: 26,
                        borderRadius: "var(--radius-sm)",
                        fontFamily: "var(--font-body)",
                      }}
                    />
                  )}
                </Box>

                <Box sx={{ borderTop: "1.5px solid var(--divider)" }} />

                <QuantityControl
                  isLogin={isLogin} isCheckStock={isCheckStock}
                  isStockAvailable={isStockAvail} stockLimit={stockLimit}
                  isStockFractional={isStockFractional}
                  qty={cantItem} increase={increase} decrease={decrease}
                  loading={loadingCart} onAddToCart={handleCartAdd}
                  onDeleteToCart={handleCartDelete} isInCart={isInCart}
                />

                {/* Observaciones */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                    <Typography sx={{ fontFamily: "var(--font-body)", fontSize: "0.78rem", fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      Observaciones
                    </Typography>

                    {/* ── Status indicator ── */}
                    {isInCart && obsStatus !== 'idle' && (
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 0.5,
                        px: 1.25, py: 0.35, borderRadius: 'var(--radius-full)',
                        transition: 'all 0.3s ease',
                        ...(obsStatus === 'typing' && { bgcolor: 'var(--bg-subtle)', border: '1px solid var(--border)', color: 'var(--text-muted)' }),
                        ...(obsStatus === 'saving' && { bgcolor: 'var(--warning-bg)', border: '1px solid var(--warning)', color: 'var(--warning)' }),
                        ...(obsStatus === 'saved' && { bgcolor: 'var(--success-bg)', border: '1px solid var(--success)', color: 'var(--success)' }),
                        ...(obsStatus === 'added' && { bgcolor: '#eff6ff', border: '1px solid #3b82f6', color: '#3b82f6' }),
                        ...(obsStatus === 'modified' && { bgcolor: '#faf5ff', border: '1px solid #7c3aed', color: '#7c3aed' }),
                        ...(obsStatus === 'deleted' && { bgcolor: 'var(--danger-bg)', border: '1px solid var(--danger)', color: 'var(--danger)' }),
                        ...(obsStatus === 'error' && { bgcolor: 'var(--danger-bg)', border: '1px solid var(--danger)', color: 'var(--danger)' }),
                      }}>
                        {obsStatus === 'typing' && <EditNoteOutlinedIcon sx={{ fontSize: 13 }} />}
                        {obsStatus === 'saving' && <CloudSyncOutlinedIcon sx={{ fontSize: 13, animation: 'spin 1s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />}
                        {obsStatus === 'saved' && <CloudDoneOutlinedIcon sx={{ fontSize: 13 }} />}
                        {obsStatus === 'added' && <CloudDoneOutlinedIcon sx={{ fontSize: 13 }} />}
                        {obsStatus === 'modified' && <CloudDoneOutlinedIcon sx={{ fontSize: 13 }} />}
                        {obsStatus === 'deleted' && <DeleteOutlineIcon sx={{ fontSize: 13 }} />}
                        {obsStatus === 'error' && <CloudSyncOutlinedIcon sx={{ fontSize: 13 }} />}
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, fontFamily: 'var(--font-body)' }}>
                          {obsStatus === 'typing' && 'Escribiendo...'}
                          {obsStatus === 'saving' && 'Guardando...'}
                          {obsStatus === 'saved' && 'Guardado'}
                          {obsStatus === 'added' && 'Observación agregada'}
                          {obsStatus === 'modified' && 'Observación modificada'}
                          {obsStatus === 'deleted' && 'Observación eliminada'}
                          {obsStatus === 'error' && 'Error al guardar'}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <TextField fullWidth multiline rows={3}
                    placeholder={isInCart ? "Agregá una nota a tu pedido..." : "Agregá el producto al carrito para escribir una observación"}
                    value={observation} disabled={!isInCart}
                    onChange={handleObsChange}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "var(--radius-md)",
                        bgcolor: isInCart ? "var(--bg-subtle)" : "var(--bg-main)",
                        fontSize: "0.875rem", fontFamily: "var(--font-body)",
                        transition: "all 0.2s ease",
                        "& fieldset": {
                          border: obsStatus === 'saving' ? '2px solid var(--warning)' :
                            (obsStatus === 'added' || obsStatus === 'modified') ? '2px solid var(--success)' :
                              obsStatus === 'deleted' ? '2px solid var(--danger)' :
                                "1.5px solid var(--border)",
                          transition: 'border-color 0.3s ease',
                        },
                        "&:hover fieldset": { borderColor: isInCart ? "var(--border-hover)" : "var(--border)" },
                        "&.Mui-focused fieldset": { borderColor: "var(--accent)", borderWidth: "2px" },
                        "&.Mui-disabled": { opacity: 0.55 },
                      },
                    }}
                  />

                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.5 }}>
                    {!isInCart
                      ? <Typography sx={{ fontSize: "0.72rem", color: "var(--text-muted)", fontFamily: "var(--font-body)", fontStyle: "italic" }}>Agregá el producto al carrito para habilitar este campo</Typography>
                      : <Typography sx={{ fontSize: "0.68rem", color: "var(--text-muted)", fontFamily: "var(--font-body)" }}>Se guarda automáticamente</Typography>
                    }
                    <Typography sx={{ fontSize: "0.72rem", color: observation.length > 900 ? 'var(--warning)' : "var(--text-muted)", fontFamily: "var(--font-mono)", fontWeight: observation.length > 900 ? 700 : 400 }}>{observation.length}/1000</Typography>
                  </Box>
                </Box>

              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>

      <Snackbar open={snackCopy} autoHideDuration={2500} onClose={() => setSnackCopy(false)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity="success" onClose={() => setSnackCopy(false)} sx={{ borderRadius: "var(--radius-md)", fontFamily: "var(--font-body)" }}>{snackMsg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default React.memo(Product);